import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import ArtifactFrame from '../components/artifacts/ArtifactFrame';
import { ArtifactApiError, ArtifactContent, getSharedArtifact } from '../shared/services/artifactsApi';
import maritacaLogoPreta from '../static/images/maritaca_logo_preta.png';

// Página pública de um artefato compartilhado (/artefato/:shareId). Uma faixa
// fina identifica a origem (conteúdo gerado por IA, de um usuário) e o resto é
// o próprio artefato, no mesmo iframe isolado do chat.

const Page = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  font-family: 'Sora', 'Lexend', system-ui, sans-serif;
`;

const Bar = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 6px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #fafafa;
  color: #1f2328;
  font-size: 0.8rem;
`;

const Logo = styled.img`
  height: 18px;
  width: auto;
  flex: 0 0 auto;
`;

const Title = styled.span`
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Note = styled.span`
  margin-left: auto;
  color: #6b7280;
  white-space: nowrap;
  @media (max-width: 640px) {
    display: none;
  }
`;

const Cta = styled.a`
  flex: 0 0 auto;
  color: #1f2328;
  font-weight: 600;
  text-decoration: none;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #d0d7de;
  @media (max-width: 640px) {
    margin-left: auto;
  }
  &:hover {
    background: #f0f1f3;
  }
`;

const Body = styled.main`
  flex: 1;
  min-height: 0;
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100%;
  padding: 24px;
  text-align: center;
  color: #374151;
  button {
    font: inherit;
    padding: 8px 16px;
    border-radius: 999px;
    border: 1px solid #d0d7de;
    background: #fff;
    cursor: pointer;
  }
`;

const SharedArtifact: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [data, setData] = useState<ArtifactContent | null>(null);
  const [error, setError] = useState<number | null>(null);

  useEffect(() => {
    if (!shareId || authLoading) return;
    let cancelled = false;
    (async () => {
      let token: string | null = null;
      if (isAuthenticated) {
        try {
          token = await getAccessTokenSilently();
        } catch { /* segue sem token */ }
      }
      try {
        const d = await getSharedArtifact(shareId, token);
        if (!cancelled) {
          setData(d);
          document.title = `${d.title} · Maritaca`;
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof ArtifactApiError ? e.status : 500);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareId, authLoading, isAuthenticated, getAccessTokenSilently]);

  let body: React.ReactNode;
  if (data) {
    body = <ArtifactFrame type={data.type} content={data.content} title={data.title} />;
  } else if (error === 401) {
    body = (
      <Message>
        <span>Este artefato foi compartilhado só com algumas pessoas. Entre para ver.</span>
        <button
          type="button"
          onClick={() => loginWithRedirect({ appState: { returnTo: window.location.pathname } })}
        >
          Entrar
        </button>
      </Message>
    );
  } else if (error === 403) {
    body = <Message>Sua conta não tem acesso a este artefato. Peça ao autor para incluir seu e-mail.</Message>;
  } else if (error === 404) {
    body = <Message>Este link não existe ou foi revogado pelo autor.</Message>;
  } else if (error) {
    body = <Message>Não foi possível carregar o artefato. Tente novamente em instantes.</Message>;
  } else {
    body = <Message>Carregando…</Message>;
  }

  return (
    <Page>
      <Bar>
        <a href="/" aria-label="Maritaca">
          <Logo src={maritacaLogoPreta} alt="Maritaca" />
        </a>
        {data && <Title title={data.title}>{data.title}</Title>}
        <Note>Criado com IA por um usuário da Maritaca. Não informe senhas nem dados pessoais.</Note>
        <Cta href="/">Criar o seu</Cta>
      </Bar>
      <Body>{body}</Body>
    </Page>
  );
};

export default SharedArtifact;
